import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { GraduationCap } from "lucide-react";
import { api } from "../services/api";
import { useUi } from "../stores/ui";
import { can, type Recruit } from "../types";
import { Card, EmptyState, Input, PageHeader, Skeleton } from "../components/ui";
import { formatSince, formatWaitTime, waitMsSince } from "../utils/waitTime";
import type { PublicSettings } from "../types";

export function RecruitsPage() {
  const user = useUi((s) => s.user);
  const [search, setSearch] = useState("");
  const settings = useQuery({
    queryKey: ["public-settings"],
    queryFn: () => api<PublicSettings>("/api/settings/public"),
  });
  const recruits = useQuery({
    queryKey: ["recruits", search],
    queryFn: () => api<{ items: Recruit[]; total: number }>(`/api/recruits?search=${encodeURIComponent(search)}`),
  });
  const timeZone = settings.data?.timezone || "America/New_York";
  const items = recruits.data?.items ?? [];
  const showId = can(user, "VIEW_MEMBER_IDS");
  const longest = useMemo(() => items[0], [items]);

  return (
    <div>
      <PageHeader
        title="Awaiting Training"
        subtitle="Recruits holding the Public Relations training-awaiting role, oldest wait first."
      />
      <Card className="mb-4">
        <Input
          placeholder="Search recruits"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Card>
      {recruits.isLoading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState title={search ? "No recruits matched your search." : "No recruits are waiting for a training."} />
      ) : (
        <>
          <div className="hidden overflow-hidden rounded-lg border border-white/10 lg:block">
            <table className="w-full text-left text-sm">
              <thead className="bg-white/5 text-zinc-400">
                <tr>
                  <th className="px-4 py-3">Recruit</th>
                  <th>Discord Username</th>
                  <th>Awaiting training</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.discordId} className="border-t border-white/5 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={item.avatarUrl} alt="" className="h-10 w-10 rounded-full" />
                        <div>
                          <p className="font-semibold">{item.displayName}</p>
                          {showId ? <p className="text-xs text-zinc-500">{item.discordId}</p> : null}
                        </div>
                      </div>
                    </td>
                    <td>@{item.username}</td>
                    <td>
                      <p className="font-medium text-accent">{formatWaitTime(waitMsSince(item.awaitingSince))}</p>
                      <p className="text-xs text-zinc-500">since {formatSince(item.awaitingSince, timeZone)}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="grid gap-3 lg:hidden">
            {items.map((item) => (
              <Card key={item.discordId} className="p-4">
                <div className="flex items-start gap-3">
                  <img src={item.avatarUrl} alt="" className="h-11 w-11 rounded-full" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold">{item.displayName}</p>
                    <p className="truncate text-sm text-zinc-500">@{item.username}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-accent">{formatWaitTime(waitMsSince(item.awaitingSince))}</p>
                    <p className="text-[11px] text-zinc-500">waiting</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          <p className="mt-4 flex items-center gap-2 text-sm text-zinc-400">
            <GraduationCap size={14} />
            {items.length} awaiting{longest ? ` · longest wait ${formatWaitTime(waitMsSince(longest.awaitingSince))}` : ""}
          </p>
        </>
      )}
    </div>
  );
}
